import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar, ChevronLeft, ChevronRight, User, Clock, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

import studyBuilderService from '../../services/studyBuilderService';
import Tooltip from '../Shared/Tooltip';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Stage color palette. Tailwind needs full class literals to retain them in the build,
// so each variant is spelled out rather than templated.
const STAGE_PALETTE = [
  { dot: 'bg-teal-500',    badge: 'bg-teal-600 text-white',         cell: 'bg-teal-500/15 dark:bg-teal-500/20',       ring: 'ring-teal-400' },
  { dot: 'bg-amber-500',   badge: 'bg-amber-500 text-white',        cell: 'bg-amber-500/15 dark:bg-amber-500/20',     ring: 'ring-amber-400' },
  { dot: 'bg-violet-500',  badge: 'bg-violet-600 text-white',       cell: 'bg-violet-500/15 dark:bg-violet-500/20',   ring: 'ring-violet-400' },
  { dot: 'bg-rose-500',    badge: 'bg-rose-500 text-white',         cell: 'bg-rose-500/15 dark:bg-rose-500/20',       ring: 'ring-rose-400' },
  { dot: 'bg-emerald-500', badge: 'bg-emerald-600 text-white',      cell: 'bg-emerald-500/15 dark:bg-emerald-500/20', ring: 'ring-emerald-400' },
  { dot: 'bg-sky-500',     badge: 'bg-sky-600 text-white',          cell: 'bg-sky-500/15 dark:bg-sky-500/20',         ring: 'ring-sky-400' },
];

const stageColor = (idx) => STAGE_PALETTE[((idx ?? 0) % STAGE_PALETTE.length + STAGE_PALETTE.length) % STAGE_PALETTE.length];

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseISODate = (s) => {
  if (!s) return null;
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day);
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const startOfWeek = (d) => addDays(d, -d.getDay());

const sameDate = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatLongDate = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const formatShortDate = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const formatMonthDay = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const daysBetween = (a, b) =>
  Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));

const describeOffset = (enrollment, studyStart) => {
  if (!enrollment || !studyStart) return '';
  const diff = daysBetween(enrollment, studyStart);
  if (diff === 0) return 'same day as enrollment';
  if (diff === 1) return '1 day after enrollment';
  if (diff < 0) return `${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'} before enrollment`;
  return `${diff} days after enrollment`;
};

const formatTargetTime = (t) => {
  // Accept "HH:MM" or "HH:MM:SS"; render as "9:00 AM"
  if (!t) return '';
  const [h, m] = t.split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

const buildScheduleConfigForPreview = (schedule, outerStudyStartDay) => {
  const config = {
    ...schedule,
    studyStartDay: outerStudyStartDay !== '' && outerStudyStartDay !== null && outerStudyStartDay !== undefined
      ? outerStudyStartDay
      : schedule.studyStartDay,
  };
  return config;
};

const summarizeScheduleType = (schedule) => {
  if (!schedule?.type) return 'Schedule';
  if (schedule.type === 'Patient Prompted (Daily)') return 'Patient Prompted (Daily)';
  return schedule.type;
};

const IMPLIED_DAILY_TYPES = ['Patient Prompted (Daily)', 'Daily Assessment'];

const summarizeCadence = (schedule) => {
  if (IMPLIED_DAILY_TYPES.includes(schedule?.type)) {
    return 'Daily surveys automatically enforced';
  }
  const stage = schedule?.stages?.[0];
  if (!stage?.frequency) return '';
  const value = stage.frequency.value || '';
  const unit = stage.frequency.unit || 'days';
  return value ? `Every ${value} ${unit}` : '';
};

const SchedulePreviewModal = ({
  open,
  onClose,
  // Preferred multi-schedule API
  schedules,
  editingSchedule,
  editingScheduleIndex,
  // Legacy single-schedule API (still supported)
  schedule: legacySchedule,
  studyStartDay,
}) => {
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);

  // Build the canonical list of schedules to switch between. When a schedule is
  // being edited, its draft form state replaces the saved copy at that index so
  // the visualizer reflects unsaved changes.
  const allSchedules = useMemo(() => {
    if (!Array.isArray(schedules) || schedules.length === 0) {
      return legacySchedule ? [legacySchedule] : (editingSchedule ? [editingSchedule] : []);
    }
    if (editingSchedule && typeof editingScheduleIndex === 'number' && editingScheduleIndex >= 0) {
      return schedules.map((s, i) => (i === editingScheduleIndex ? editingSchedule : s));
    }
    return schedules;
  }, [schedules, editingSchedule, editingScheduleIndex, legacySchedule]);

  const [activeIdx, setActiveIdx] = useState(() =>
    typeof editingScheduleIndex === 'number' && editingScheduleIndex >= 0 ? editingScheduleIndex : 0
  );
  const activeSchedule = allSchedules[activeIdx] || allSchedules[0];

  const [enrollmentDate, setEnrollmentDate] = useState(isoDate(today));
  const [view, setView] = useState('week');
  const [focusDate, setFocusDate] = useState(today);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIso, setSelectedIso] = useState(null);
  const [filterStageIdx, setFilterStageIdx] = useState(null); // null = all stages

  const browserTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );

  // Reset state each time the modal opens
  useEffect(() => {
    if (open) {
      const todayIso = isoDate(today);
      setEnrollmentDate(todayIso);
      setView('week');
      setFocusDate(today);
      setPreview(null);
      setSelectedIso(null);
      setFilterStageIdx(null);
      setActiveIdx(
        typeof editingScheduleIndex === 'number' && editingScheduleIndex >= 0 ? editingScheduleIndex : 0
      );
    }
  }, [open, today, editingScheduleIndex]);

  // When switching between schedules, drop any open day detail / stage filter
  useEffect(() => {
    setSelectedIso(null);
    setFilterStageIdx(null);
    setPreview(null);
  }, [activeIdx]);

  useEffect(() => {
    if (!open || !activeSchedule || !enrollmentDate) return;

    const config = buildScheduleConfigForPreview(activeSchedule, studyStartDay);
    let cancelled = false;
    setLoading(true);
    studyBuilderService
      .previewSchedule(config, enrollmentDate, browserTz)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        const enrolled = parseISODate(data.enrollment_date);
        if (enrolled) setFocusDate(enrolled);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'Failed to preview schedule';
        toast.error(msg);
        setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, activeSchedule, studyStartDay, enrollmentDate, browserTz]);

  const occurrencesByDate = useMemo(() => {
    const map = new Map();
    if (preview?.occurrences) {
      for (const occ of preview.occurrences) {
        // Apply stage filter: keep only timepoints from the filtered stage
        if (filterStageIdx !== null) {
          const filteredTps = occ.timepoints.filter((tp) => tp.stage_index === filterStageIdx);
          if (filteredTps.length === 0) continue;
          map.set(occ.date, {
            ...occ,
            timepoints: filteredTps,
            stage_indices: [filterStageIdx],
          });
        } else {
          map.set(occ.date, occ);
        }
      }
    }
    return map;
  }, [preview, filterStageIdx]);

  const enrollmentDateObj = preview ? parseISODate(preview.enrollment_date) : parseISODate(enrollmentDate);
  const studyStartDateObj = preview ? parseISODate(preview.study_start_date) : null;
  const selectedOccurrence = selectedIso ? occurrencesByDate.get(selectedIso) : null;
  const selectedDateObj = selectedIso ? parseISODate(selectedIso) : null;

  const visibleDays = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(focusDate);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    const monthStart = new Date(focusDate.getFullYear(), focusDate.getMonth(), 1);
    const gridStart = startOfWeek(monthStart);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [view, focusDate]);

  const headerLabel = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(focusDate);
      return `Week of ${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()}, ${start.getFullYear()}`;
    }
    return `${MONTHS[focusDate.getMonth()]} ${focusDate.getFullYear()}`;
  }, [view, focusDate]);

  const stepCalendar = (direction) => {
    if (view === 'week') {
      setFocusDate((d) => addDays(d, 7 * direction));
    } else {
      setFocusDate((d) => new Date(d.getFullYear(), d.getMonth() + direction, 1));
    }
  };

  const handleCellClick = (iso, hasOccurrence) => {
    if (!hasOccurrence) return;
    setSelectedIso((curr) => (curr === iso ? null : iso));
    const target = parseISODate(iso);
    if (target) setFocusDate(target);
  };

  const surveyName = (sid) => preview?.surveys?.[sid]?.name || `Survey ${sid.slice(0, 6)}…`;
  const stageName = (idx) => preview?.stages?.find((s) => s.index === idx)?.name || `Stage ${idx + 1}`;

  if (!open) return null;

  const stages = preview?.stages || [];
  const summary = preview?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-h-[95vh] flex flex-col transition-all ${selectedOccurrence ? 'max-w-6xl' : 'max-w-4xl'}`}>
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-3 rounded-t-2xl bg-gradient-to-r from-teal-600 to-blue-600 shrink-0">
          <div className="flex items-center gap-3 text-white">
            <Calendar className="w-5 h-5" />
            <div>
              <h2 className="text-lg font-semibold leading-tight">Schedule Visualizer</h2>
              <p className="text-xs text-white/80">
                {summary && summary.total_survey_instances > 0 ? (
                  <>
                    <strong>{summary.total_survey_instances}</strong> survey{summary.total_survey_instances === 1 ? '' : 's'}
                    {' · '}
                    <strong>{summary.stage_count}</strong> stage{summary.stage_count === 1 ? '' : 's'}
                    {' · '}
                    <strong>{summary.span_days}</strong> day{summary.span_days === 1 ? '' : 's'}
                    {summary.first_survey_date && summary.last_survey_date && (
                      <>
                        {' · '}
                        {formatMonthDay(parseISODate(summary.first_survey_date))}
                        {' → '}
                        {formatMonthDay(parseISODate(summary.last_survey_date))}
                      </>
                    )}
                  </>
                ) : (
                  'Preview participant study timeline'
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1"
            aria-label="Close visualizer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-3 space-y-3 overflow-y-auto">
          {/* Schedule picker (shown only when there are multiple schedules) */}
          {allSchedules.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 shrink-0">
                Schedule
              </span>
              <div className="flex flex-wrap gap-1.5">
                {allSchedules.map((sch, idx) => {
                  const isActive = idx === activeIdx;
                  const label = sch?.name?.trim() || summarizeScheduleType(sch) || `Schedule ${idx + 1}`;
                  const arms = Array.isArray(sch?.arms) ? sch.arms : [];
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveIdx(idx)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                        isActive
                          ? 'bg-teal-600 border-teal-600 text-white'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="font-medium truncate max-w-[160px]">{label}</span>
                      {arms.length > 0 && (
                        <span className="flex items-center gap-1">
                          {arms.map((arm) => (
                            <span
                              key={arm}
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-none ${
                                isActive
                                  ? 'bg-white/20 text-white'
                                  : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                              }`}
                            >
                              {arm}
                            </span>
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top info row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border border-purple-200 dark:border-purple-900 bg-purple-50/40 dark:bg-purple-900/10 rounded-lg p-2">
              <Tooltip
                content="Simulate a participant's enrollment date to preview how this schedule will roll out for them. Change the date to see how the study start, stages, and survey windows shift based on the current schedule configuration."
                iconClassName="w-3.5 h-3.5 text-gray-500 hover:text-gray-700 ml-1"
              >
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                  <User className="w-3.5 h-3.5" />
                  Enrollment Date
                </label>
              </Tooltip>
              <input
                type="date"
                value={enrollmentDate}
                onChange={(e) => setEnrollmentDate(e.target.value)}
                className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {enrollmentDateObj && (
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                  {formatShortDate(enrollmentDateObj)}
                </p>
              )}
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50/40 dark:bg-gray-900/40">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-200">Schedule</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                <span className="font-medium text-gray-700 dark:text-gray-200">{summarizeScheduleType(activeSchedule)}</span>
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">{summarizeCadence(activeSchedule)}</p>
              {Array.isArray(activeSchedule?.arms) && activeSchedule.arms.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {activeSchedule.arms.map((arm) => (
                    <span
                      key={arm}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 leading-none"
                    >
                      {arm}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {studyStartDateObj && (
              <div className="border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-900/10 rounded-lg p-2">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-blue-600" />
                  Study Start (Day {preview.study_start_day})
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                  <strong className="text-gray-700 dark:text-gray-200">{formatShortDate(studyStartDateObj)}</strong>
                  {' — '}{describeOffset(enrollmentDateObj, studyStartDateObj)}
                </p>
              </div>
            )}
          </div>

          {/* Stage overview strip */}
          {stages.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  Stages
                  {filterStageIdx !== null && (
                    <span className="ml-2 text-[11px] font-normal text-gray-500 dark:text-gray-400">
                      (filtered to {stageName(filterStageIdx)})
                    </span>
                  )}
                </p>
                {filterStageIdx !== null && (
                  <button
                    onClick={() => setFilterStageIdx(null)}
                    className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Show all
                  </button>
                )}
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {stages.map((stage) => {
                  const color = stageColor(stage.index);
                  const isFiltered = filterStageIdx === stage.index;
                  const dimmed = filterStageIdx !== null && !isFiltered;
                  return (
                    <button
                      key={stage.index}
                      onClick={() => setFilterStageIdx(isFiltered ? null : stage.index)}
                      className={`w-full flex items-center gap-3 px-3 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                        dimmed ? 'opacity-50' : ''
                      } ${isFiltered ? 'bg-gray-100 dark:bg-gray-700/60' : ''}`}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${color.dot} shrink-0`}></span>
                      <span className="font-medium text-gray-700 dark:text-gray-200 min-w-[80px] truncate">
                        {stage.name}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        Day {stage.start_day}–{stage.end_day}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {stage.frequency_label}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 ml-auto whitespace-nowrap">
                        {stage.occurrence_count} occurrence{stage.occurrence_count === 1 ? '' : 's'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calendar + detail (side-by-side when a day is selected) */}
          <div className={`grid gap-3 ${selectedOccurrence ? 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px]' : 'grid-cols-1'}`}>
            <div className="space-y-2 min-w-0">
          {/* Calendar header */}
          <div className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
            <div className="flex items-center gap-2">
              <button
                onClick={() => stepCalendar(-1)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{headerLabel}</span>
              <button
                onClick={() => stepCalendar(1)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setView('week')}
                className={`px-3 py-1 text-sm ${
                  view === 'week'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setView('month')}
                className={`px-3 py-1 text-sm border-l border-gray-200 dark:border-gray-700 ${
                  view === 'month'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Month
              </button>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-900 text-xs font-medium text-gray-500 dark:text-gray-400">
              {WEEKDAYS.map((wd) => (
                <div key={wd} className="px-2 py-1 text-center">{wd}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {visibleDays.map((d) => {
                const iso = isoDate(d);
                const occ = occurrencesByDate.get(iso);
                const isEnrollment = enrollmentDateObj && sameDate(d, enrollmentDateObj);
                const isStudyStart = studyStartDateObj && sameDate(d, studyStartDateObj);
                const isLastSurvey = summary?.last_survey_date && iso === summary.last_survey_date;
                const inFocusMonth = view === 'month' ? d.getMonth() === focusDate.getMonth() : true;
                const isSelected = selectedIso === iso;
                const stageIndices = occ?.stage_indices || [];
                const primaryColor = stageIndices.length > 0 ? stageColor(stageIndices[0]) : null;

                const cellHeight = view === 'month' ? 'min-h-[58px]' : 'min-h-[68px]';
                const stageBg = primaryColor ? primaryColor.cell : '';
                const selectedRing = isSelected ? 'ring-2 ring-blue-500 ring-inset' : '';
                const enrollmentRing = isEnrollment && !isSelected ? 'ring-2 ring-purple-400 ring-inset' : '';

                return (
                  <div
                    key={iso}
                    onClick={() => handleCellClick(iso, !!occ)}
                    className={`${cellHeight} border-t border-l border-gray-100 dark:border-gray-700 p-1 text-xs flex flex-col gap-0.5 ${
                      inFocusMonth ? '' : 'bg-gray-50 dark:bg-gray-900/40 text-gray-400'
                    } ${stageBg} ${enrollmentRing} ${selectedRing} ${occ ? 'cursor-pointer hover:brightness-110' : ''}`}
                  >
                    <div className="flex items-center justify-between leading-none">
                      <span className={`font-medium ${inFocusMonth ? 'text-gray-700 dark:text-gray-200' : ''}`}>
                        {d.getDate()}
                      </span>
                      {occ && (
                        <span className="text-[9px] text-gray-500 dark:text-gray-400">
                          D{occ.day_number}
                        </span>
                      )}
                    </div>
                    {isStudyStart && (
                      <span className="inline-block px-1 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 leading-none">
                        Start
                      </span>
                    )}
                    {isLastSurvey && (
                      <span className="inline-block px-1 py-0.5 rounded text-[9px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 leading-none">
                        End
                      </span>
                    )}
                    {occ && stageIndices.slice(0, 2).map((sIdx) => {
                      const c = stageColor(sIdx);
                      const label = stageName(sIdx);
                      return (
                        <span
                          key={sIdx}
                          className={`inline-block px-1 py-0.5 rounded text-[9px] font-medium ${c.badge} leading-none truncate`}
                          title={label}
                        >
                          {label}
                        </span>
                      );
                    })}
                    {occ && stageIndices.length > 2 && (
                      <span className="text-[9px] text-gray-500 dark:text-gray-400 leading-none">
                        +{stageIndices.length - 2} more
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
            </div>

            {/* Day detail side panel */}
            {selectedOccurrence && selectedDateObj && (
              <div className="border border-blue-200 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-900/10 rounded-lg p-3 lg:max-h-[440px] lg:overflow-y-auto">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-100 truncate">
                      {formatShortDate(selectedDateObj)}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Day {selectedOccurrence.day_number} · Visit {selectedOccurrence.visit_number}
                    </p>
                    {selectedOccurrence.stage_indices?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {selectedOccurrence.stage_indices.map((idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300"
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${stageColor(idx).dot}`}></span>
                            {stageName(idx)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedIso(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5 shrink-0"
                    aria-label="Close detail"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {selectedOccurrence.timepoints.map((tp, tpIdx) => {
                    const c = stageColor(tp.stage_index);
                    return (
                      <div
                        key={`${tp.id}-${tpIdx}`}
                        className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 p-2"
                      >
                        <div className="flex items-center gap-2 mb-1 min-w-0">
                          <span className={`w-2 h-2 rounded-full ${c.dot} shrink-0`}></span>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate flex-1">
                            {tp.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                          <Clock className="w-3 h-3" />
                          {formatTargetTime(tp.target_time)}
                          <span className="text-gray-400 dark:text-gray-500">
                            · {tp.window_minutes}m window
                          </span>
                        </div>
                        <ul className="pl-1 space-y-0.5">
                          {tp.survey_ids.length === 0 && (
                            <li className="text-[11px] text-gray-400 italic">No surveys assigned</li>
                          )}
                          {tp.survey_ids.map((sid) => (
                            <li
                              key={sid}
                              className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300"
                            >
                              <FileText className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="truncate">{surveyName(sid)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full ring-2 ring-purple-400"></span>
              Enrollment
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-blue-100 dark:bg-blue-900/40"></span>
              Study Start
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-red-100 dark:bg-red-900/40"></span>
              Last Survey
            </span>
            <span className="text-gray-400 dark:text-gray-500">·</span>
            <span className="text-gray-500 dark:text-gray-400">
              Click any survey day for details · Click a stage to filter
            </span>
            {loading && (
              <span className="ml-auto text-gray-500 dark:text-gray-400">Computing…</span>
            )}
            {!loading && preview && preview.occurrences.length === 0 && (
              <span className="ml-auto text-gray-500 dark:text-gray-400">
                No surveys scheduled — check stage day bounds and frequency.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulePreviewModal;
